import { rateLimitedFetchAuthSession } from "@/setup/frontend/fetchAuthSession";
import { useRef, useState } from "react";

import { queryClient } from "@/setup/frontend/queryClient";
import {
	httpBatchLink,
	httpLink,
	httpSubscriptionLink,
	isNonJsonSerializable,
	type Operation,
	retryLink,
	splitLink,
	type TRPCClient,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import type { ApiRouter } from "@/setup/backend/trpc/trpcApiRouter";
import { EventSourcePolyfill } from "event-source-polyfill";
//@ts-ignore
BigInt.prototype.toJSON = function () {
	return this.toString();
};

export const trpc = createTRPCReact<ApiRouter>();

export type ApiClient = TRPCClient<ApiRouter>;

export type TrpcUtils = ReturnType<typeof trpc.useUtils>;
export const {
	TRPCProvider: TRPCProviderV2,
	useTRPC,
	useTRPCClient,
} = createTRPCContext<ApiRouter>();

function getUrl() {
	return `${import.meta.env.VITE_API_URL}/api/trpc`;
}

async function getHeaders(opts: { op: Operation } | { opList: Operation[] }) {
	const session = await rateLimitedFetchAuthSession().catch(() => null);
	const token = session?.tokens?.accessToken.toString();
	const headers = {
		Authorization:
			token && token !== "undefined" ? `Bearer ${token}` : undefined,
		"x-vcg-mobile-app":
			import.meta.env.VITE_CAPACITOR_BUILD === "true" ? "true" : "false",
		"x-vcg-app-version": import.meta.env.VITE_APP_VERSION || "unknown",
		"x-vcg-offline":
			"op" in opts
				? opts.op.type === "mutation" && opts.op.context?.offline
					? "true"
					: "false"
				: "opList" in opts
					? opts.opList.some(
							(op) => op.type === "mutation" && op.context?.offline,
						)
						? "true"
						: "false"
					: "false",
		"x-vcg-offline-timestamp":
			"op" in opts
				? opts.op.type === "mutation" && opts.op.context?.offlineTimestamp
					? opts.op.context.offlineTimestamp
					: undefined
				: "opList" in opts
					? opts.opList.find(
							(op) => op.type === "mutation" && op.context?.offlineTimestamp,
						)?.context?.offlineTimestamp
					: undefined,
	};
	return headers as Record<string, string>;
}

let subscriptionRetryCount = 0;

export const trpcClient = trpc.createClient({
	links: [
		splitLink({
			condition: (op) => {
				if (op.type === "subscription") {
					return false;
				}

				return true;
			},
			false: [
				retryLink({
					retry: (opts) => {
						if (subscriptionRetryCount > 3) {
							return false;
						}
						subscriptionRetryCount++;
						const code = opts.error.data?.code;
						if (!code) {
							// This shouldn't happen as our httpSubscriptionLink will automatically retry within when there's a non-parsable response
							console.error("No error code found, retrying", opts);
							return true;
						}
						if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
							console.log("Retrying due to 401/403 error");
							return true;
						}
						return false;
					},
				}),
				httpSubscriptionLink({
					url: getUrl(),
					EventSource: EventSourcePolyfill,
					eventSourceOptions: async (opts) => ({
						headers: await getHeaders(opts),
					}),
				}),
			],
			true: splitLink({
				condition: (op) => {
					if (op.input instanceof FormData) {
						return false;
					}
					if (isNonJsonSerializable(op.input)) {
						return false;
					}
					return true;
				},
				false: httpLink({
					url: getUrl(),
					headers: getHeaders,
					async fetch(url, options) {
						if (options?.body instanceof FormData) {
							const headers = await getHeaders({ opList: [] }); // for backwards compatibility
							const requestOptions = {
								method: options.method,
								body: options.body,
								headers: headers, // Use the headers directly. Note: idk why we're not using options.headers here. @TODO figure out why this is needed.
								credentials: "include" as const,
							};

							return fetch(url, requestOptions);
						}

						return fetch(url, {
							...options,
							credentials: "include",
						});
					},
				}),
				true: httpBatchLink({
					url: getUrl(),
					headers: getHeaders,
				}),
			}),
		}),
	],
});
export function TRPCProvider(
	props: Readonly<{
		children: React.ReactNode;
	}>,
) {
	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<TRPCProviderV2 trpcClient={trpcClient} queryClient={queryClient}>
				{props.children}
			</TRPCProviderV2>
		</trpc.Provider>
	);
}
